import {KpisList} from '@domain/kpis'
import {HierarchyNodeMetadata} from '@domain/shared'
import {Comments, CommonComment} from '@hconnect/common/comments'
import {type TimeZone} from '@hconnect/common/types'
import {formatNumber} from '@hconnect/common/utils'
import {DateRange, formatFloat, formatTimeZoneDate} from '@hconnect/uikit'
import {KpiScore} from '@hconnect/uikit/src/lib2'
import CloseIcon from '@mui/icons-material/Close'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import {Box, IconButton, Typography} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {AxiosError} from 'axios'
import moment from 'moment-timezone'
import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useCreateComment} from '../hooks/api/useCreateComment'
import {Comment, CommentLocationData} from '../types'
import {getComparisonDate, getLocationEntryWithParentsLabel} from '../utils'

type CommentDialogProps = {
  handleClose: () => void
  topic: KpisList
  comments: Comment[]
  commentLocationData: CommentLocationData
  hierarchy?: HierarchyNodeMetadata
  dateRange: DateRange<Date>
  timezone: TimeZone
  onError?: (error: AxiosError) => void
}

const DATE_FORMAT = 'DD.MM.YYYY'

type CommentItem = CommonComment & {
  userSelectedTimestampFrom: string
  userSelectedTimestampTo: string
  delta: number
}

export const CommentDialog = ({
  handleClose,
  commentLocationData,
  topic,
  hierarchy,
  comments,
  dateRange,
  timezone,
  onError
}: CommentDialogProps) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {to, from} = dateRange

  const {mutate} = useCreateComment(commentLocationData.locationData.id, {onError})

  const data = useMemo<CommentItem[]>(
    () =>
      comments.map((comment) => ({
        id: comment.id,
        text: comment.text,
        createdOn: moment.utc(comment.createdAt),
        createdBy: comment.creatorName || '',
        userSelectedTimestampFrom: comment.userSelectedTimestampFrom,
        userSelectedTimestampTo: comment.userSelectedTimestampTo,
        delta: comment.delta
      })),
    [comments]
  )

  const getCommentPath = useCallback(
    (commentId: string) => {
      const comment = comments.find(({id}) => id === commentId)

      if (comment && hierarchy) {
        const commentLocation = getLocationEntryWithParentsLabel(
          commentLocationData.locationData.id,
          comment.locationId,
          hierarchy
        )
        return (
          commentLocation && (
            <Typography
              variant="caption"
              display="inline-flex"
              data-test-id={`comment-${commentId}-path`}
            >
              <LocationOnOutlinedIcon sx={{fontSize: 16}} />
              {commentLocation}
            </Typography>
          )
        )
      }
    },
    [commentLocationData.locationData.id, comments, hierarchy]
  )

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="sm" data-test-id="comment-dialog">
      <Box display="flex" alignItems="center" justifyContent="space-between" pr={3} gap={1}>
        <DialogTitle variant="h3" data-test-id="comments-dialog-title">
          {commentLocationData.locationData.name} - {t(`actualVsPlanned.label.${topic}`)}
        </DialogTitle>
        <IconButton aria-label="close" onClick={handleClose} data-test-id="comments-dialog-close">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent>
        <Box display="flex" justifyContent="center" gap={2}>
          <KpiScore
            value={formatNumber(commentLocationData.plannedCurrentValue, language)}
            unit={commentLocationData.unit}
            label={t('actualVsPlanned.label.plannedDate', {
              date: getComparisonDate({to, from}, language)
            })}
            type="boxed"
            data-test-id="comment-dialog-planned-current-value"
          />
          <KpiScore
            value={formatNumber(commentLocationData.actualCurrentValue, language)}
            unit={commentLocationData.unit}
            label={t('actualVsPlanned.label.actualDate', {
              date: getComparisonDate({to, from}, language)
            })}
            type="boxed"
            data-test-id="comment-dialog-actual-current-value"
          />
          <KpiScore
            value={formatFloat(commentLocationData.delta, 1)}
            unit="%"
            label={t('actualVsPlanned.label.delta')}
            type="boxed"
            data-test-id="comment-dialog-delta"
          />
        </Box>
        <Comments
          data={data}
          isLoading={false}
          onAddComment={(text) => {
            mutate({
              text,
              topic,
              delta: commentLocationData.delta,
              userSelectedTimestampFrom: from.toJSON(),
              userSelectedTimestampTo: to.toJSON()
            })
          }}
          renderPrefix={getCommentPath}
          timeZone={timezone}
          hideDivider={true}
          showCounter={true}
          translate={t}
          language={language}
          customCommentPostfix={(comment) => (
            <Typography variant="caption">
              {t('comments.description', {
                by: comment.updatedBy ?? comment.createdBy,
                on: formatTimeZoneDate(
                  comment.updatedOn?.toISOString() ?? comment.createdOn.toISOString(),
                  timezone,
                  DATE_FORMAT,
                  language
                ),
                timePeriod: `${formatTimeZoneDate(
                  comment.userSelectedTimestampFrom,
                  timezone,
                  DATE_FORMAT,
                  language
                )} - ${formatTimeZoneDate(
                  comment.userSelectedTimestampTo,
                  timezone,
                  DATE_FORMAT,
                  language
                )}`,
                delta: `${formatFloat(comment.delta, 1, language)}%`
              })}
            </Typography>
          )}
        />
      </DialogContent>
    </Dialog>
  )
}
